import React from "react";
import PropTypes from "prop-types";
import {
  Typography,
  TextField,
  Autocomplete,
  Chip,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import professionTypes from "../../../data/proffesionTypes.json";
import qualification from "../../../data/qualification.json";
import StepNavigationButtons from "../../../components/Reusables/StepControls/StepNavigationButtons";
import FormCollectionDatePicker from "../../../components/Reusables/FormControls/FormCollectionDatePicker";
import FieldLabel from "./FieldLabel";
import {
  BASIC_TEXT_FIELD_SX,
  AUTOCOMPLETE_MENU_SX,
  AUTOCOMPLETE_INPUT_BASE_SX,
} from "../onboardingStyles";

const qualificationOptions = qualification.map((item) => ({
  label: item,
  value: item,
}));

const ProfileStep = ({
  formData2,
  setFormData2,
  errors,
  onBack,
  onProceed,
  loading,
}) => (
  <div>
    <Typography
      variant="h6"
      sx={{ fontSize: "20px", fontWeight: 700, color: "var(--app-text)" }}
    >
      Complete Account Details
    </Typography>

    <Typography
      sx={{
        fontSize: "16px",
        fontWeight: 500,
        color: "var(--app-text-muted)",
        mt: 0.5,
      }}
    >
      Finish setting up your account for a smooth and personalized experience.
    </Typography>
    <br />
    <div className="row">
      <div className="col-12">
        <div className="form-group">
          <FieldLabel required sx={{ mt: 0.5, mb: 0 }}>
            Gender
          </FieldLabel>

          <TextField
            select
            fullWidth
            value={formData2.gender || ""}
            onChange={(e) =>
              setFormData2((prev) => ({
                ...prev,
                gender: e.target.value,
              }))
            }
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) {
                  return (
                    <span style={{ color: "var(--app-text-muted)" }}>
                      Select Gender
                    </span>
                  );
                }
                return selected;
              },
            }}
            sx={BASIC_TEXT_FIELD_SX}
          >
            {/*  Placeholder item */}
            <MenuItem value="" disabled>
              Select Gender
            </MenuItem>

            {["Female", "Male", "Other"].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          {errors.gender && (
            <small className="text-danger">{errors.gender}</small>
          )}
        </div>
      </div>

      <div className="col-12">
        <div className="form-group">
          <FormCollectionDatePicker
            label="Date of Birth"
            required={true}
            value={formData2.dob}
            onChange={(date) =>
              setFormData2((prev) => ({
                ...prev,
                dob: date,
              }))
            }
            errors={errors.dob}
          />
        </div>
      </div>

      <div className="col-12">
        <div className="form-group">
          <FieldLabel required sx={{ mt: 0.5, mb: 0 }}>
            Profession
          </FieldLabel>

          <Autocomplete
            options={professionTypes}
            value={formData2.profession || null}
            onChange={(event, value) =>
              setFormData2((prev) => ({
                ...prev,
                profession: value,
              }))
            }
            freeSolo={false}
            clearOnEscape
            disablePortal
            popupIcon={<KeyboardArrowDownIcon />}
            ListboxProps={{
              style: {
                maxHeight: "250px",
                overflowY: "auto",
              },
            }}
            sx={AUTOCOMPLETE_MENU_SX}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Profession"
                fullWidth
                error={false}
                sx={{
                  ...AUTOCOMPLETE_INPUT_BASE_SX,
                  "& input": {
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "var(--app-text)",
                  },
                }}
              />
            )}
          />

          {errors.profession && (
            <small className="text-danger">{errors.profession}</small>
          )}
        </div>
      </div>

      <div className="col-md-12">
        <div className="form-group">
          <FieldLabel sx={{ mt: 0.5, mb: 0 }}>Qualification</FieldLabel>
          <Autocomplete
            multiple
            options={qualificationOptions.map((d) => d.label)}
            value={formData2.qualification || []}
            onChange={(event, value) =>
              setFormData2((prev) => ({
                ...prev,
                qualification: value,
              }))
            }
            limitTags={2}
            disableCloseOnSelect
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option}
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                  sx={{
                    fontSize: 13,
                    borderRadius: 2,
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined" // important
                placeholder="Select Qualification"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: "52px",
                    backgroundColor: "var(--app-surface)",
                    borderRadius: "10px",
                    fontFamily: "figtree",
                    padding: "6px 10px",
                  },

                  "& .MuiOutlinedInput-root fieldset": {
                    border: "1px solid var(--app-border)",
                  },

                  "& .MuiOutlinedInput-root:hover fieldset": {
                    border: "1px solid var(--app-border)",
                  },

                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    border: "1px solid var(--app-border)",
                  },

                  "& .MuiInputBase-input": {
                    padding: "8px 4px !important",
                  },
                }}
              />
            )}
          />

          {errors.qualification && (
            <small className="text-danger">{errors.qualification}</small>
          )}
        </div>
      </div>

      <div className="col-12">
        <div className="form-group">
          <FieldLabel sx={{ mt: 0.5, mb: 0 }}>
            Medical License Number (if applicable)
          </FieldLabel>

          <TextField
            fullWidth
            placeholder="Enter Medical License Number"
            value={formData2.medical_license_number}
            onChange={(e) =>
              setFormData2((prev) => ({
                ...prev,
                medical_license_number: e.target.value,
              }))
            }
            sx={BASIC_TEXT_FIELD_SX}
          />
        </div>
      </div>
      <StepNavigationButtons
        onBack={onBack}
        onProceed={onProceed}
        loading={loading}
      />
    </div>
  </div>
);

ProfileStep.propTypes = {
  formData2: PropTypes.object.isRequired,
  setFormData2: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default ProfileStep;
